import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getRequestData } from 'api/api';
import { endpoints } from 'api/endpoints';
import {
  SelectOption,
  IconPlus,
  OrangeButton,
  IconPen,
  IconFilter,
  IconSmallArrow,
  CustomTable,
  DownloadClientsCSV,
  IconSearch,
  SmallButton,
  IconReset,
  IconSoberLiving,
  IconChild,
  IconNoRelation,
  IconEye,
} from 'components';
import { COLORS } from 'utils/colors';
import { PF25_ADMIN_STATUS, ROLE_TYPES, SLH_TYPES } from 'api/constants';
import { Input } from 'antd';
import DefaultAvatar from 'assets/images/default-avatar.png';
import { useUser } from 'context/userContext';
import { AddEditPFManagerModal } from 'components/addEditPFManagerModal';
import { useAuth } from 'context/authContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SlhSelect from 'components/slhSelect';

export const SlhManagers = () => {
  const { userDetails } = useUser();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { partnerId, isParentManaging, parentId } = useAuth();
  const [managers, setManagers] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [partners, setPartners] = useState([]);
  const isInitialPartnerApplied = useRef(false);
  const latestRequestId = useRef(0);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    gender: '',
  });

  const searchWord = searchParams.get('search') || '';
  const selectedStatus = searchParams.get('status') || null;
  const selectedPartner = searchParams.get('partner') || '';

  const updateQueryParams = useCallback(
    (updates, options = {}) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }
      });

      setSearchParams(next, options);
    },
    [searchParams, setSearchParams],
  );

  const fetchSLHManagers = useCallback(async (params) => {
    const requestId = latestRequestId.current + 1;
    latestRequestId.current = requestId;

    const { data } = await getRequestData(endpoints.getSLHManagers(params), ROLE_TYPES.slh_manager);
    if (requestId !== latestRequestId.current) {
      return;
    }

    if (data) {
      setManagers({
        ...data,
        results: Array.isArray(data.results) ? [...data.results] : [],
      });
    }
  }, []);

  useEffect(() => {
    if (userDetails?.partner_details?.type === SLH_TYPES.parent || isParentManaging) {
      (async () => {
        const { data } = await getRequestData(endpoints.getAllPartners(), ROLE_TYPES.slh_manager);
        if (data) {
          setPartners(data);
        }
      })();
    }
  }, [isParentManaging, userDetails]);

  const handleSearch = (e) => {
    const searchTerm = e.target.value;
    updateQueryParams(
      {
        search: searchTerm || null,
        page: null,
      },
      { replace: true },
    );
  };

  const handleStatusSelectChange = (status) => {
    updateQueryParams({ status, page: null });
  };

  const handlePartnerSelectChange = (partner) => {
    updateQueryParams({ partner, page: null });
  };

  const handleFiltersReset = useCallback(() => {
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  useEffect(() => {
    if (isInitialPartnerApplied.current) return;

    if (!searchParams.get('partner') && isParentManaging && partnerId) {
      updateQueryParams({ partner: partnerId, page: null }, { replace: true });
      isInitialPartnerApplied.current = true;
      return;
    }

    isInitialPartnerApplied.current = true;
  }, [isParentManaging, partnerId, searchParams, updateQueryParams]);

  const fetchManagersByQuery = useCallback(() => {
    const page = Number(searchParams.get('page')) || 1;
    const filters = { page };

    if (selectedStatus) {
      filters.status = selectedStatus;
    }

    if (searchWord) {
      filters.search = searchWord;
    }

    if (selectedPartner) {
      filters.partner_ids = selectedPartner;
    }

    fetchSLHManagers(filters);
  }, [fetchSLHManagers, searchParams, searchWord, selectedPartner, selectedStatus]);

  useEffect(() => {
    fetchManagersByQuery();
  }, [fetchManagersByQuery]);

  const handlePaginationChange = (page) => {
    updateQueryParams({ page });
  };

  const storeBtn = useMemo(() => {
    if (selectedStatus || searchWord || (selectedPartner && +selectedPartner !== +parentId)) {
      return <SmallButton onClick={handleFiltersReset} label="Reset All" icon={<IconReset />} />;
    }
    return null;
  }, [selectedStatus, searchWord, selectedPartner, parentId, handleFiltersReset]);

  const columns = [
    {
      title: 'Manager',
      dataIndex: 'manager',
      width: 350,
      render: (_, record) => (
        <div
          className={`flex items-center gap-x-1 ${record.status === PF25_ADMIN_STATUS.inactive ? 'pointer-events-none opacity-50' : ''}`}
        >
          <img
            className="w-[30px] h-[30px] rounded-full"
            src={record.avatar ? record.avatar : DefaultAvatar}
            alt="Resident"
          />
          <div className="flex flex-col ml-2">
            <p className="text-14 mb-0.5">
              {record.firstname} {record.lastname}
            </p>
            <p className="text-12 text-lightBlue font-normal">{record.email}</p>
          </div>
        </div>
      ),
    },
    {
      title: 'SLH',
      dataIndex: 'slh',
      render: (_, record) => (
        <Link
          to={`/partner/view/${record.partner.id}`}
          className="text-14 font-medium overflow-hidden whitespace-nowrap gap-x-1 flex items-center pr-4 capitalize"
        >
          {record.partner.type === SLH_TYPES.parent ? (
            <IconSoberLiving />
          ) : record.partner.type === SLH_TYPES.child ? (
            <IconChild />
          ) : (
            <IconNoRelation />
          )}
          {record.partner.name}
        </Link>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'role',
      render: () => (
        <p className="text-14 font-medium overflow-hidden whitespace-nowrap pr-4 capitalize">
          Manager
        </p>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      render: (_, record) => (
        <p className="text-14 font-medium overflow-hidden whitespace-nowrap pr-4">
          {record.phone ? `${record.country_code}${record.phone}` : '-'}
        </p>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (status) => (
        <p className="text-14 capitalize font-medium overflow-hidden">{status}</p>
      ),
    },
    {
      title: 'Action',
      width: 50,
      dataIndex: 'id',
      render: (_, record) => (
        <div className="flex items-center gap-x-2">
          <IconEye
            color="#4318FF"
            className={`w-8 h-8 p-1 ml-[auto] bg-light rounded-[8px] cursor-pointer`}
            onClick={() => navigate(`/slh-manager-profile/${record.id}`)}
          />
          <IconPen
            color="#4318FF"
            className={`w-8 h-8 p-1 ml-[auto] bg-light rounded-[8px] cursor-pointer ${record.id === userDetails?.user_id ? 'pointer-events-none opacity-50' : ''}`}
            onClick={() => {
              setIsModalOpen(true);
              setIsEditMode(true);
              setFormData(record);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    managers && (
      <div className="flex flex-col w-full gap-y-6">
        <div className="flex justify-between w-full">
          <p className="nunito-title">SLH Managers</p>
          <div className="flex items-center gap-x-6">
            <Input
              className="search w-[300px] text-lightBlue text-16 rounded-ten bg-light"
              type="text"
              value={searchWord}
              placeholder="Search"
              addonBefore={<IconSearch color={COLORS.lightBlue} />}
              onChange={handleSearch}
            />
            <OrangeButton
              onClick={() => setIsModalOpen(true)}
              label="Add new manager"
              icon={<IconPlus />}
            />
            <DownloadClientsCSV list={managers.results} />
          </div>
        </div>
        {
          <>
            <div className="flex items-center gap-x-3">
              <div className="flex items-center gap-x-1 mr-3">
                <IconFilter />
                <p className="text-16">Filter</p>
              </div>
              <SelectOption
                onSelect={(value) => handleStatusSelectChange(value)}
                dropdownStyle={{ padding: '16px', background: 'white' }}
                virtual={false}
                required
                value={selectedStatus}
                suffixIcon={
                  <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
                }
                className="h-[40px] text-base font-medium base-block rounded-lg"
                popupClassName="min-w-[180px]"
                placeholder="Status"
                dropdownRender={(originalNode) => originalNode}
                optionRender={(option) => (
                  <div
                    className={`flex items-center px-2 py-1 gap-x-1 text-black rounded-md capitalize border border-color-divider bg-white overflow-hidden ${selectedStatus === option.value ? 'border-orange' : ''}`}
                  >
                    <span
                      className={`w-6 h-6  rounded-full border border-orange ${selectedStatus === option.value ? 'border-[6px]' : ''}`}
                    />
                    <p className="font-14 flex-1">{option.label}</p>
                  </div>
                )}
                options={[
                  {
                    label: <p className="text-14 px-4 py-1.5 rounded-md">Active</p>,
                    value: PF25_ADMIN_STATUS.active,
                  },
                  {
                    label: <p className="text-14 px-4 py-1.5 rounded-md">Inactive</p>,
                    value: PF25_ADMIN_STATUS.inactive,
                  },
                  {
                    label: <p className="text-14 px-4 py-1.5 rounded-md">Pending</p>,
                    value: PF25_ADMIN_STATUS.pending,
                  },
                ]}
              />
              {(userDetails?.partner_details?.type === SLH_TYPES.parent || isParentManaging) && (
                <SlhSelect
                  data={partners}
                  value={
                    selectedPartner
                      ? +selectedPartner === +parentId
                        ? +parentId
                        : +selectedPartner
                      : ''
                  }
                  onChange={(id) => handlePartnerSelectChange(id)}
                />
              )}
              {storeBtn}
            </div>
            <CustomTable
              columns={columns}
              data={managers.results}
              onChange={handlePaginationChange}
              rowKey={(record) => record?.id}
              pageCount={managers.pageCount}
              current={managers.currentPage}
              pageSize={managers.pageSize}
            />
          </>
        }
        {isModalOpen &&
          (isEditMode ? (
            <AddEditPFManagerModal
              isOpen={isModalOpen}
              onClose={() => {
                setIsModalOpen(false);
                setIsEditMode(false);
              }}
              onSuccess={fetchManagersByQuery}
              adminData={formData}
              isEditMode={isEditMode}
              role={ROLE_TYPES.slh_manager}
              partnerID={userDetails?.partner_details?.id}
              partners={
                userDetails?.partner_details?.type === SLH_TYPES.parent
                  ? partners
                  : {
                      label: userDetails?.partner_details?.name,
                      value: userDetails?.partner_details?.id,
                    }
              }
              partnerDetails={userDetails?.partner_details}
            />
          ) : (
            <AddEditPFManagerModal
              partnerID={userDetails?.partner_details?.id}
              isOpen={isModalOpen}
              role={ROLE_TYPES.slh_manager}
              onClose={() => setIsModalOpen(false)}
              onSuccess={fetchManagersByQuery}
              partners={partners}
              partnerDetails={userDetails?.partner_details}
            />
          ))}
      </div>
    )
  );
};
